import React from 'react';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { timingFunctions, directions, fillModes, previewObjects } from '../data/mock';
import { Code2 } from 'lucide-react';
import { cn } from '../lib/utils';

const ControlGroup = ({ label, children }) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
      {label}
    </label>
    {children}
  </div>
);

const ControlsPanel = ({ settings, onSettingsChange, previewObject, onPreviewObjectChange }) => {
  const handleChange = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  return (
    <aside className="w-[300px] bg-[#0F172A] border-r border-white/5 flex flex-col h-full overflow-y-auto no-scrollbar shadow-2xl relative z-20">
      <div className="p-6 border-b border-white/5 flex items-center justify-between">
        <Label className="text-xs font-black uppercase tracking-[0.2em] text-white">Config</Label>
        <button
          onClick={() => onSettingsChange({
            duration: 500,
            delay: 0,
            iterationCount: 1,
            direction: 'normal',
            timingFunction: 'ease',
            fillMode: 'both',
          })}
          className="text-[10px] font-black text-slate-500 hover:text-violet-400 transition-all uppercase tracking-widest"
        >
          Reset
        </button>
      </div>

      <div className="p-8 space-y-10 flex-1">
        {/* Object Selector */}
        <ControlGroup label="Object Model">
          <Select value={previewObject.id} onValueChange={(value) => {
            const obj = previewObjects.find(o => o.id === value);
            if (obj) onPreviewObjectChange(obj);
          }}>
            <SelectTrigger className="w-full bg-white/[0.03] border-white/10 text-slate-300 h-11 font-bold text-xs shadow-none hover:bg-white/[0.06] transition-all rounded-xl focus:ring-violet-500/20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-300 rounded-xl overflow-hidden shadow-2xl">
              {previewObjects.map(obj => (
                <SelectItem key={obj.id} value={obj.id} className="text-xs font-black uppercase tracking-widest py-3 focus:bg-violet-600 focus:text-white cursor-pointer">
                  {obj.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlGroup>

        {/* Duration */}
        <ControlGroup label={`Speed (${settings.duration}ms)`}>
          <Slider
            value={[settings.duration]}
            onValueChange={(value) => handleChange('duration', value[0])}
            min={100}
            max={3000}
            step={50}
            className="[&_[role=slider]]:bg-white [&_[role=slider]]:border-violet-500 [&_[role=slider]]:shadow-[0_0_10px_rgba(139,92,246,0.5)] [&_[role=slider]]:h-5 [&_[role=slider]]:w-5"
          />
        </ControlGroup>

        {/* Easing */}
        <ControlGroup label="Motion Curve">
          <Select value={settings.timingFunction} onValueChange={(value) => handleChange('timingFunction', value)}>
            <SelectTrigger className="w-full bg-white/[0.03] border-white/10 text-slate-300 h-11 font-bold text-xs shadow-none hover:bg-white/[0.06] rounded-xl focus:ring-violet-500/20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-300 max-h-60 rounded-xl shadow-2xl">
              {timingFunctions.map(tf => (
                <SelectItem key={tf.id} value={tf.value} className="text-xs font-black uppercase tracking-widest py-3 focus:bg-violet-600 focus:text-white cursor-pointer">
                  {tf.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlGroup>

        {/* Delay */}
        <ControlGroup label={`Initial Delay (${settings.delay}ms)`}>
          <Slider
            value={[settings.delay]}
            onValueChange={(value) => handleChange('delay', value[0])}
            min={0}
            max={2000}
            step={50}
            className="[&_[role=slider]]:bg-white [&_[role=slider]]:border-violet-500 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5"
          />
        </ControlGroup>

        {/* Iteration */}
        <ControlGroup label="Playback Loops">
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map(n => (
              <button
                key={n}
                onClick={() => handleChange('iterationCount', n)}
                className={cn(
                  "h-10 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border",
                  settings.iterationCount === n
                    ? "bg-violet-600 border-violet-500 text-white shadow-lg shadow-violet-500/20"
                    : "bg-white/[0.03] border-white/5 text-slate-500 hover:bg-white/[0.08] hover:text-slate-300"
                )}
              >
                {n}x
              </button>
            ))}
            <button
              onClick={() => handleChange('iterationCount', 'infinite')}
              className={cn(
                "col-span-3 h-10 mt-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border",
                settings.iterationCount === 'infinite'
                  ? "bg-violet-600 border-violet-500 text-white shadow-lg shadow-violet-500/20"
                  : "bg-white/[0.03] border-white/5 text-slate-500 hover:bg-white/[0.08] hover:text-slate-300"
              )}
            >
              Infinite Loop
            </button>
          </div>
        </ControlGroup>

        {/* Direction & Fill Mode */}
        <div className="space-y-8 pt-4">
          <ControlGroup label="Flow Strategy">
            <Select value={settings.direction} onValueChange={(value) => handleChange('direction', value)}>
              <SelectTrigger className="w-full bg-white/[0.03] border-white/10 text-slate-300 h-11 font-bold text-xs uppercase shadow-none rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-slate-300 rounded-xl">
                {directions.map(dir => (
                  <SelectItem key={dir.id} value={dir.name} className="text-xs font-black uppercase tracking-widest py-3 focus:bg-violet-600 focus:text-white cursor-pointer">
                    {dir.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ControlGroup>

          <ControlGroup label="End State">
            <Select value={settings.fillMode} onValueChange={(value) => handleChange('fillMode', value)}>
              <SelectTrigger className="w-full bg-white/[0.03] border-white/10 text-slate-300 h-11 font-bold text-xs uppercase shadow-none rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-slate-300 rounded-xl">
                {fillModes.map(fm => (
                  <SelectItem key={fm.id} value={fm.name} className="text-xs font-black uppercase tracking-widest py-3 focus:bg-violet-600 focus:text-white cursor-pointer">
                    {fm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ControlGroup>
        </div>
      </div>

      <div className="p-8 border-t border-white/5 bg-white/[0.02]">
        <button className="w-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white py-4 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-[0_10px_30px_-10px_rgba(139,92,246,0.5)] hover:shadow-violet-500/60 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3">
          <Code2 className="w-4 h-4" />
          Export System
        </button>
      </div>
    </aside>
  );
};

export default ControlsPanel;
